import { useState, useEffect, useMemo } from "react";
import { useAbortedEffect, useHookForm } from "../../../../components/hooks";
import { useDispatch } from "react-redux";
import { setProgress, setToast } from "../../../../redux/slice/system";
import { RecordAPI } from "../../../../axios/api";
import moment from "moment";

import ModulePopConfirm from "../../../../components/pop/popConfirm";
import {
  FormCalendar,
  FormRadio,
  FormDropdown,
} from "../../../../components/formElement";
import { formatResData, checkFormUpdate } from "../../../../service/common";
import { rateHandler } from "../common";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";

export default function ModulePopRecordHealthForm({
  prePopOption,
  closePrePop,
  openPrePop,
  setPreReload,
  memberid,
  moduleType,
  memberData,
}) {
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState({});
  // 表單欄位預設值
  const defaultValues = {
    date: moment().format("YYYY/MM/DD"),
    q1: null,
    q2: null,
    q3: null,
    q4: null,
    q5: null,
    memberid: null,
  };
  const {
    handleSubmit,
    getValues,
    reset,
    watch,
    yup,
    setSchema,
    errors,
    setting,
  } = useHookForm({
    defaultValues,
  });
  const scoreData = [
    { label: "健康飲食", key: "q1" },
    { label: "運動", key: "q2" },
    { label: "紓壓", key: "q3" },
    { label: "拒菸害", key: "q4" },
    { label: "按時服藥", key: "q5" },
  ];
  // 監聽欄位
  const watchColumn = {
    q1: watch("q1"),
    q2: watch("q2"),
    q3: watch("q3"),
    q4: watch("q4"),
    q5: watch("q5"),
  };

  useEffect(() => {
    setSchema(
      yup
        .object({
          memberid: yup
            .number()
            .test("isValidPass", "必填", (value) => {
              return moduleType === "back" && !memberid ? value : true;
            })
            .nullable(),
          date: yup.string().required("必填"),
          q1: yup.number().required("必填"),
          q2: yup.number().required("必填"),
          q3: yup.number().required("必填"),
          q4: yup.number().required("必填"),
          q5: yup.number().required("必填"),
        })
        .required()
    );
  }, []);

  const totalScore = useMemo(() => {
    let submitData = getValues();

    return (
      (submitData.q1 ?? 0) +
      (submitData.q2 ?? 0) +
      (submitData.q3 ?? 0) +
      (submitData.q4 ?? 0) +
      (submitData.q5 ?? 0)
    );
  }, [
    watchColumn.q1,
    watchColumn.q2,
    watchColumn.q3,
    watchColumn.q4,
    watchColumn.q5,
  ]);

  useAbortedEffect(
    (signal) => {
      if (!prePopOption.data) return;

      dispatch(setProgress(true));

      let params = { id: prePopOption.data.id };

      RecordAPI.getRecordHealthBehaviorDetails({
        data: params,
        options: { signal },
      }).then((result) => {
        let { Response, data, message } = result;

        if (Response === 1) {
          let temp = formatResData(defaultValues, data);
          setApiData(temp);
          reset(temp);
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: message,
              detail: "",
            })
          );
        }

        dispatch(setProgress(false));
      });
    },
    [prePopOption.data]
  );

  const onSubmit = (submitData) => {
    dispatch(setProgress(true));

    let isAdd = !prePopOption.data,
      jsonData = formatResData(defaultValues, submitData);
    if (jsonData.date)
      jsonData.date = moment(jsonData.date).format("YYYY-MM-DD");

    if (!checkFormUpdate(jsonData, prePopOption.data ?? {})) {
      dispatch(
        setToast({
          severity: "warn",
          summary: "無資料變更",
          detail: "",
        })
      );
      dispatch(setProgress(false));
      closePrePop({ type: "form" });
      return;
    }

    let promise,
      toast = "";
    if (isAdd) {
      // 新增
      promise = RecordAPI.setRecordHealthBehaviorData({
        data: {
          ...jsonData,
          memberid: submitData.memberid ?? memberid,
        },
      });
      toast = "新增";
    } else {
      // 修改
      if (jsonData.date === prePopOption.data.date) delete jsonData.date;
      promise = RecordAPI.updateRecordHealthBehaviorData({
        data: {
          ...jsonData,
          id: prePopOption.data.id,
        },
      });
      toast = "修改";
    }

    promise.then(async (result) => {
      let { Response, my_old_day_data, my_todays_data, message } = result;

      if (Response === 1) {
        await rateHandler({
          memberid: submitData.memberid ?? memberid,
          my_old_day_data,
          my_todays_data,
          old_date: isAdd ? null : prePopOption.data.date,
          now_date: submitData.date,
        });

        dispatch(
          setToast({
            severity: "success",
            summary: `${toast}成功`,
            detail: "",
          })
        );
        closePrePop({ type: "form" });
        setPreReload((state) => ({ ...state, list: true }));

        // 檢查資料異常值，跳出警示通知
        if (
          jsonData.q1 <= 10 ||
          jsonData.q2 <= 10 ||
          jsonData.q3 <= 10 ||
          jsonData.q4 <= 10 ||
          jsonData.q5 <= 10
        ) {
          openPrePop({
            type: "confirm",
            data: {
              msg: "請養成均衡飲食、規律運動、適時放鬆、遠離菸害並按時服藥的好習慣。每個小行動都能讓您更健康，若有困難歡迎諮詢，我們樂意協助您！",
            },
          });
        }
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: `${toast}失敗`,
            detail: message ?? "",
          })
        );
        dispatch(setProgress(false));
      }
    });
  };

  return (
    <>
      <div className="px-4">
        <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid">
          {moduleType === "back" && !memberid && (
            <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
              <label
                htmlFor="memberid"
                className={classNames(
                  { "p-error": errors.memberid },
                  "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
                )}
              >
                <span className="text-red-400 mr-1">*</span>
                會員
              </label>
              <div className="col-12 sm:col-9">
                <FormDropdown
                  setting={setting}
                  data={{
                    name: "memberid",
                    options: memberData,
                    optionLabel: "name",
                    optionValue: "id",
                    filter: true,
                    filterBy: "name",
                    disabled: prePopOption.data,
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="date"
              className={classNames(
                { "p-error": errors.date },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              <span className="text-red-400 mr-1">*</span>
              日期
            </label>
            <div className="col-12 sm:col-9">
              <FormCalendar
                setting={setting}
                data={{
                  name: "date",
                  // disabled: true,
                }}
              />
            </div>
          </div>

          <div className="col-12 text-xl font-bold text-blue-400">
            以下護心健康行為您今天做到多少程度?
          </div>

          <div className="col-12 white-space-prewrap my-2">
            {`建議目標\n飲食：低鈉、低脂/膽固醇\n運動：中度活動30分鐘/天\n紓壓：放鬆練習20分鐘/天\n菸品：拒菸\n藥物：按醫囑服用`}
          </div>

          <div className="col-12 mt-1">
            <table className="w-full table-collapse text-center" border="1">
              <thead className="bg-blue-50">
                <tr>
                  <th></th>
                  <th className="p-2">完全沒執行</th>
                  <th className="p-2">部分執行</th>
                  <th className="p-2">大部分執行</th>
                  <th className="p-2">完全執行</th>
                </tr>
                <tr>
                  <th></th>
                  <th className="p-2">0-25分</th>
                  <th className="p-2">26-50分</th>
                  <th className="p-2">51-75分</th>
                  <th className="p-2">76-100分</th>
                </tr>
              </thead>
              <tbody>
                {scoreData.map((score) => (
                  <tr key={`score_${score.key}`}>
                    <td className="text-left font-bold bg-blue-50 p-2">
                      {score.label}
                    </td>
                    <td className="p-2">
                      <FormRadio
                        className="justify-content-center m-0"
                        setting={setting}
                        data={{
                          name: score.key,
                          label: "",
                          value: 5,
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <FormRadio
                        className="justify-content-center m-0"
                        setting={setting}
                        data={{
                          name: score.key,
                          label: "",
                          value: 10,
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <FormRadio
                        className="justify-content-center m-0"
                        setting={setting}
                        data={{
                          name: score.key,
                          label: "",
                          value: 15,
                        }}
                      />
                    </td>
                    <td className="p-2">
                      <FormRadio
                        className="justify-content-center m-0"
                        setting={setting}
                        data={{
                          name: score.key,
                          label: "",
                          value: 20,
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="col-12 text-xl font-bold mt-2">
            總分：{totalScore}
          </div>
          <div
            className={classNames("col-12 text-xl font-bold mt-2", {
              "text-green-400": totalScore >= 55,
              "text-orange-400": totalScore < 55,
            })}
          >
            {totalScore === 0
              ? ""
              : totalScore >= 80
              ? "您真是太棒了"
              : totalScore >= 55
              ? "您很棒喔"
              : totalScore >= 25
              ? "繼續加油喔"
              : ""}
          </div>

          <div className="col-12 text-right mt-3">
            <Button
              type="button"
              label="取消"
              className="p-button-secondary p-button-outlined ml-auto px-3"
              onClick={() => closePrePop({ type: "form" })}
            />
            <Button type="submit" label="儲存" className="ml-2 px-3" />
          </div>
        </form>
      </div>
    </>
  );
}
